/**
 * Global registry of IDs used for analytics, HelpHero or other external tools.
 *
 * IDs placed in this list are to be treated as if they are an API, meaning if you change them,
 * you are likely going to break an integration with a 3rd party tool.
 */
interface IdRegistry {
    // Tenant Apps widget lifecycle. Emitted by @luminovo/slots from the PRODUCT
    // page, never from the widget document — the apps origin has no Segment and
    // its CSP is `connect-src 'self'`, so the product page is the only place that
    // already holds a consented, identified analytics client.
    //
    // `app`/`widget` are ids from the slot-assignments response, `slot` the
    // surface, and `universal` separates staff-authored `lumi-*` apps served to
    // every tenant from a tenant's own. Deliberately no app VERSION: the
    // assignments response does not carry one today.
    //
    // `channel` separates an author previewing an unreleased version (`?lumi_draft`)
    // from a user on the released one. It is a dimension rather than a filter
    // because this event is the platform-adoption denominator: dropping draft views
    // would undercount nothing, but folding them in would count a preview as a
    // tenant using the app.
    /**
     * A button an app contributed was offered to someone — the menu holding it
     * rendered. Once per button per page session, so it counts reach rather than
     * re-renders, and is the denominator for `apps/button_clicked`.
     *
     * Its own event rather than `apps/widget_viewed`: that one measures dwell on a
     * frame, and folding a menu entry into it would change what the adoption metric
     * for widgets means.
     */
    ['apps/button_shown']: { app: string; widget: string; slot: string; universal: boolean };
    /** Someone pressed it. The adoption metric for this surface. */
    ['apps/button_clicked']: { app: string; widget: string; slot: string; universal: boolean };
    /**
     * How the run ended. `file` is the outcome the surface exists for, so it is
     * separated from a run that merely succeeded; `status` carries the HTTP code on
     * `failed` and is absent otherwise.
     */
    ['apps/button_completed']: {
        app: string;
        widget: string;
        slot: string;
        universal: boolean;
        outcome: 'file' | 'done' | 'failed' | 'navigated';
        status?: number;
    };
    ['apps/slot_assignments_loaded']: { slot: string; assignment_count: number; channel: 'release' | 'draft' };
    ['apps/widget_failed']: {
        app: string;
        widget: string;
        slot: string;
        universal: boolean;
        reason: 'session_unavailable' | 'ready_timeout' | 'load_error' | 'session_recovery_exhausted';
    };
    /** Dwell. Paired with `apps/widget_viewed`, emitted when the widget stops being visible. */
    ['apps/widget_hidden']: { app: string; widget: string; slot: string; universal: boolean; visible_ms: number };
    ['apps/widget_navigated']: { app: string; widget: string; slot: string; universal: boolean };
    /** The frame completed its handshake. Reliability/latency signal, and the denominator for `apps/widget_viewed`. */
    ['apps/widget_rendered']: { app: string; widget: string; slot: string; universal: boolean; ready_ms: number };
    /** A rendered widget was actually on screen long enough to count as seen. The adoption metric. */
    ['apps/widget_viewed']: { app: string; widget: string; slot: string; universal: boolean };
    /** Sent to login from a signed-in page: opened signed out, session ended at renewal, or a boundary caught a 401. */
    ['auth/email_form_redirected']: { source: 'before_load' | 'provider' | 'error_fallback' };
    ['auth/email_form_rendered']: {};
    ['auth/email_form_submitted']: {};
    ['auth/login_hint_error']: { error: string };
    ['auth/login_hint_success']: { connectionType: string };
    ['auth/login_success']: { email: string };
    ['auth/login_with_redirect_success']: { connectionType: string };
    ['auth/login_failed']: {};
    ['auth/customer_signup_page']: {};
    ['bom_importer/box_header']: {};
    ['bom_importer/button_import']: {};
    ['bom_importer/button_export']: {};
    ['bom_importer/image_drag_and_drop']: {};
    ['bom_importer/table_header']: {};
    ['design/assembly_monitoring_drawer']: {};
    ['design/box_manufacturer_free']: {};
    ['design/box_pcb_availability_notification']: {};
    ['design/box_pcb_price_availability_notification']: {};
    ['design/box_pcb_specification']: {};
    ['design/box_pcb_characteristics']: {};
    ['design/box_pcb_appearance']: {};
    ['design/box_pcb_advanced']: {};
    ['design/box_pcb_markings']: {};
    ['design/box_pcb_price_radar']: {};
    ['design/box_pcb_reports']: {};
    ['design/box_pcb_specification_form']: {};
    ['design/box_pcb_type']: {};
    ['design/box_pcb_stack_up']: {};
    ['design/box_pcb_advanced_stack_up']: {};
    ['design/button_add_assembly']: {};
    ['design/button_add_comment']: {};
    ['design/button_add_custom_part_manually']: {};
    ['design/button_add_generic_part']: {};
    ['design/button_add_ots_part_manually']: {};
    ['design/button_add_part_manually']: {};
    ['design/button_add_part_suggestion']: {};
    ['design/button_add_part_via_ipn']: {};
    ['design/button_add_part_via_mpn']: {};
    ['design/button_add_placed_component']: {};
    ['design/button_alternative_parts']: {};
    ['design/button_assembly_change_history']: {};
    ['design/button_assembly_monitoring']: {};
    ['design/button_assembly']: {};
    ['design/button_bom_change_history']: {};
    ['design/button_compare_parts']: {};
    ['design/button_contact_ems']: {};
    ['design/button_custom']: {};
    ['design/button_delete_assembly']: {};
    ['design/button_delete_bom']: {};
    ['design/button_dowload_original_bom']: {};
    ['design/button_edit_assembly']: {};
    ['design/button_edit_bom_item']: {};
    ['design/button_export_bom']: {};
    ['design/button_filter_by_all']: {};
    ['design/button_filter_by_approved']: {};
    ['design/button_filter_by_dnp']: {};
    ['design/button_filter_by_pending']: {};
    ['design/button_filter_by_rejected']: {};
    ['design/button_manufacturing_data']: {};
    ['design/button_off_the_shelf']: {};
    ['design/button_pcb_file_manager_edit']: {};
    ['design/button_pcb_file_manager_save']: {};
    ['design/button_pcb_specification_save']: {};
    ['design/button_part_selection']: {};
    ['design/button_partial_matches']: {};
    ['design/button_pin_section']: {};
    ['design/button_request_quotation']: {};
    ['design/button_search']: {};
    ['design/button_suggestions']: {};
    ['design/image_upload_pcb_files']: {};
    ['design/input_notes']: {};
    ['design/input_pcb_specification_description']: {};
    ['design/label_stock']: {};
    ['design/left_sidebar']: {};
    ['design/message_no_part_options']: {};
    ['design/pcb-home-tab']: {};
    ['design/pcb-layer-tab']: {};
    ['design/pcb-drills-tab']: {};
    ['design/pcb-stack-up-tab']: {};
    ['design/pcb-specification-tab']: {};
    ['design/pcb-panelization-tab']: {};
    ['design/pcb-dfm-tab']: {};
    ['design/pcb-cad-viewer-tab']: {};
    ['design/pcb-dfm-analysis-tab']: {};
    ['design/section_add_parts']: {};
    ['design/section_selected_part_options']: {};
    ['design/section_suggestions']: {};
    ['design/select_component_type']: {};
    ['design/select_sourced_by']: {};
    ['design/tab_bom']: {};
    ['design/tab_cad']: {};
    ['design/tab_files']: {};
    ['design/tab_pcb']: {};
    ['design/tab_pnp']: {};
    ['design/warning_filters']: {};
    ['home_navbar/link_manufacturing_lite']: {};
    // Lumi Agent page usage, sent by the page itself. `conversation_id` is a random id per chat that ties its events
    // together; it names no project or user. Never sent: message text, file names or contents, tool arguments or
    // results, entity ids.
    /**
     * The page opened. `source` is how: its row in the sidebar, the shortcut, the command palette, or a URL (a link, a
     * bookmark, a new tab). A reload counts again, with the source it was opened by. `from_route` is the route template
     * of the page before, such as `/projects/$projectId/bom`, without its ids; absent for `url`.
     */
    ['lumi_agent/opened']: { source: 'sidebar' | 'shortcut' | 'palette' | 'url'; from_route?: string };
    /**
     * A user turn left. `turn` counts from 1 within the chat, `mode` is `ask_before_changes` or
     * `change_without_asking`, `attachments` counts the files by media type (`{ 'application/pdf': 1 }`), and
     * `has_page_context` says whether the message carried the page the Lumi Agent was opened from (its chip).
     */
    ['lumi_agent/message_sent']: {
        conversation_id: string;
        turn: number;
        mode: string;
        attachments: Record<string, number>;
        has_page_context: boolean;
    };
    /**
     * A turn ended. `outcome` is `answered`, `stopped` (Stop, Esc, New chat, or leaving the page cut the run off) or
     * the endpoint's error code (`rate`, `mcp_unavailable`, or `run_failed` and `turn_timed_out` when the stream broke
     * after the answer began; `http_<status>` when it sent none, `error` when no code arrived, such as a network
     * failure). A run that stops on a card to approve is not the end. `duration_ms` includes the time spent on cards,
     * which `approvals_requested` counts. A retry after an error is the same `turn`, times its own run, and ends with
     * its own event. `model` is absent when the turn ended before an answer started.
     */
    ['lumi_agent/answer_finished']: {
        conversation_id: string;
        turn: number;
        outcome: string;
        duration_ms: number;
        tool_calls: number;
        approvals_requested: number;
        model?: string;
    };
    /** Approve or Deny on a card. `tool` is the tool's name. */
    ['lumi_agent/approval_decided']: { conversation_id: string; tool: string; approved: boolean };
    /**
     * New chat, leaving the page, or closing the tab. `turns` is 0 for a chat that never got a message, and
     * `duration_ms` runs from the chat opening (the page, or New chat), not from its first message.
     */
    ['lumi_agent/chat_left']: { conversation_id: string; turns: number; duration_ms: number };
    ['manufacturing_scenario/activities_table']: {};
    ['manufacturing_scenario/button_add_manufacturing_scenario_dialog_continue_with_template']: {};
    ['manufacturing_scenario/button_create_manufacturing_scenario']: {};
    ['manufacturing_scenario/button_view_activities']: {};
    ['manufacturing_scenario/button_view_expenses']: {};
    ['manufacturing_scenario/cost_breakdown_table']: {};
    ['manufacturing_scenario/dialog_add_manufacturing_scenario_assembly']: {};
    ['manufacturing_scenario/dialog_add_manufacturing_scenario_select_template']: {};
    ['manufacturing_scenario/drivers_list']: {};
    ['manufacturing_scenario/expenses_table']: {};
    ['manufacturing_scenario/manufacturing_scenarios_table_card']: {};
    ['manufacturing_scenario/manufacturing_scenarios_table']: {};
    ['manufacturing_scenario/order_size_column']: {};
    ['navbar/button_avatar']: {};
    ['navbar/button_dashboard']: {};
    ['navbar/button_design']: {};
    ['navbar/button_notifications']: {};
    ['navbar/link_manufacturing_lite']: {};
    ['navbar/link_manufacturing']: {};
    ['oem_create_rfq/section_additional_services']: {};
    ['oem_create_rfq/button_save']: {};
    ['oem_create_rfq/form_item_industry']: {};
    ['oem_create_rfq/notes']: {};
    ['oem_create_rfq/order_scenarios']: {};
    ['oem_create_rfq/project_name']: {};
    ['oem_dashboard/button_rfq_menu']: {};
    ['oem_dashboard/button_contact_ems']: {};
    ['oem_dashboard/button_edit_rfq']: {};
    ['oem_dashboard/button_request_quotation']: {};
    ['oem_dashboard/button_request_technical_support']: {};
    ['oem_dashboard/button_upload_bom']: {};
    ['oem_dashboard/button_upload_cad']: {};
    ['oem_dashboard/button_upload_files']: {};
    ['oem_dashboard/button_upload_manufacturing_files']: {};
    ['oem_dashboard/button_upload_pcb']: {};
    ['oem_dashboard/button_upload_pnp']: {};
    ['project_menu/button_share']: {};
    ['rfq_overview/button_view_archive']: {};
    ['rfq_overview/form_item_search_rfqs']: {};
    ['risk_management/button_monitoring']: {};
    ['risk_management/monitoring_drawer']: {};
    ['sourcing/apply_sourcing_scenario-changes']: {};
    ['sourcing/form_purchase_orders']: {};
    ['sourcing/button_create_version']: {};
    ['sourcing/button_import_pdf']: {};
    ['sourcing/button_request_quote']: {};
    ['sourcing/button_submit_form']: {};
    ['sourcing/card_version_history']: {};
    ['sourcing/input_supplier']: {};
    ['suppliers/button_add_supplier']: {};
    ['settings/e2e_manufacturing_scenario_templates_container']: {};
    ['settings/e2e_calculation_templates_container']: {};
    ['settings/e2e_show_part_data_warnings_to_customers']: {};
    ['checkout/e2e_stripe_payment_element']: {};
    ['checkout/e2e_stripe_address_element']: {};
    ['checkout/e2e_show_part_data_warnings_to_customers']: {};
    ['settings/e2e_selected_template']: {};
    ['custom-part-offer-importer/start']: {};
    ['custom-part-offer-importer/success']: {};
    ['custom-part-offer-importer/error']: {};
}

type Section =
    | 'apps'
    | 'auth'
    | 'bom_importer' // sort alphabetically
    | 'checkout'
    | 'custom-part-offer-importer'
    | 'design'
    | 'home_navbar'
    | 'lumi_agent'
    | 'manufacturing_scenario'
    | 'navbar'
    | 'oem_create_rfq'
    | 'oem_dashboard'
    | 'project_menu'
    | 'rfq_overview'
    | 'risk_management'
    | 'settings'
    | 'sourcing'
    | 'suppliers';
type ID = `${Section}/${string}`;

export type RegisteredId = ID & keyof IdRegistry;

/**
 * The properties a given registered id expects.
 *
 * `analytics.track` takes a loose `object`, so the shapes above are documentation
 * for most call sites. This exposes them to the type system for callers that want
 * the registry to actually check what they send — see `@luminovo/slots`, whose
 * events are emitted from library code no reviewer reads alongside the registry.
 */
export type IdProperties<T extends RegisteredId> = IdRegistry[T];

/**
 * Use this function when you want to mark an ID as relevant to 3rd party tools (e.g. HelpHero tours).
 *
 * @see IdRegistry
 */
export function id(id: RegisteredId): RegisteredId {
    return id;
}
